// Per rule: think-in-systems (ai.knowledge/global) — one definition of
// "does this registry section group have anything to show", shared by the
// sub-section strip that renders the empty state and by the panel that decides
// whether to mount that strip at all. Two copies of this predicate is how the
// strip and its host come to disagree, and a disagreement here is exactly the
// "No settings available" banner above working fields.
// Per rule: reference-rules-in-comments (ai.knowledge/global) — rule ref above.

import type { InterfaceCondition } from '#~/room/components/image/settings/conditions.js';
import type { DeviceRegistryInterfaceSection } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { DeviceRegistryInterfaceItemControlType } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep.js';

/**
 * Whether an interface section has at least one item a user can actually see.
 *
 * Three things make an item invisible: a registry condition that hides it, an
 * empty label (the Device Registry cannot express the label for some command
 * kinds yet, and an unlabelled control renders as a blank row), and being a
 * nested child with no multi-control parent in the section — `Controls` only
 * draws nested items through a MULTI_CONTROL parent's pass list, so a section
 * holding nothing but orphaned children has no rows at all. A section whose
 * every item is invisible is not "a section with hidden bits" — it is an empty
 * section, and must not be offered to the user.
 *
 * @param section - The interface section to check
 * @param conditionsMap - The conditions map for visibility/disabled state
 * @returns True if the section has at least one visible item
 */
export const sectionHasVisibleItems = (
  section: DeviceRegistryInterfaceSection,
  conditionsMap: Record<number, InterfaceCondition>
): boolean => {
  const sectionItems = Object.entries(section.items || {});
  if (sectionItems.length === 0) return false;

  // Nested children render only under a MULTI_CONTROL parent (see Controls);
  // without one in this section they are invisible however well-labelled.
  const hasMultiControlParent = sectionItems.some(
    ([_, item]) =>
      !item.nested && item.controlType === DeviceRegistryInterfaceItemControlType.MULTI_CONTROL
  );

  // Check if any items are actually visible (not hidden by conditions)
  return sectionItems.some(([_, item]) => {
    const condition = conditionsMap[item.key];
    const isHiddenByCondition = condition?.isHidden ?? false;
    if (isHiddenByCondition) return false;
    if (item.nested) return hasMultiControlParent;

    return item.label !== ''; // TODO: this is because strings aren't supported yet within the DR
  });
};

/**
 * Narrows an ordered list of section labels to those with something to render.
 *
 * The label list comes from `useSortedSectionsForSectionGroupKey`, which orders
 * by sort weight and only requires a section to HAVE a label — it says nothing
 * about the section's items. This is the filter that decides what the user sees,
 * so callers that must agree with the rendered strip should call it rather than
 * re-deriving "visible" from the same raw inputs.
 *
 * @param sectionLabels - Ordered section labels for one section group
 * @param interfaceSections - The device's interface sections, keyed by id
 * @param conditionsMap - The conditions map for visibility/disabled state
 * @returns The subset of `sectionLabels` that has at least one visible item, in order
 */
export const selectVisibleSectionLabels = (
  sectionLabels: ReadonlyArray<string>,
  interfaceSections: ReadonlyObjectDeep<Record<string, DeviceRegistryInterfaceSection>> | undefined,
  conditionsMap: Record<number, InterfaceCondition>
): Array<string> => {
  // O(1) lookup by label instead of a find() per candidate label.
  const sectionsByLabel = new Map<string, DeviceRegistryInterfaceSection>();

  Object.values(interfaceSections ?? {}).forEach((section) => {
    if (section.label) {
      sectionsByLabel.set(section.label, section);
    }
  });

  return sectionLabels.filter((label) => {
    const matchingSection = sectionsByLabel.get(label);

    if (!matchingSection) return false;

    return sectionHasVisibleItems(matchingSection, conditionsMap);
  });
};
